import React, { useCallback, useState } from 'react';
import { ArrowRightIcon, ExpandIcon } from 'lucide-react';
import { galleryPage, GalleryProject } from '../data/gallery';
import { Modal } from '../components/Modal';
export function GalleryPage() {
  const {
    hero,
    projects,
    recognition
  } = galleryPage;
  const [activeId, setActiveId] = useState<string | null>(null);
  const close = useCallback(() => setActiveId(null), []);
  const active = projects.find(p => p.id === activeId);
  const [featured, ...supporting] = projects;
  return <>
      {/* Hero */}
      <section id="top" aria-labelledby="gallery-title" className="bg-ivory pb-10 pt-10 md:pb-12 md:pt-14 lg:pb-14 lg:pt-16" data-id="element-591" data-mp="1307tofhlzyl9">
        <div className="site-container grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-14" data-id="element-592" data-mp="t44jqngimvnr">
          <div className="lg:col-span-7" data-id="element-593" data-mp="39srt41wpl961">
            <p className="eyebrow text-moss" data-id="element-594" data-mp="39rp1tsvh54pl">{hero.eyebrow}</p>
            <h1 id="gallery-title" className="mt-5 font-display text-[42px] font-medium leading-[1.03] tracking-[-0.01em] text-charcoal sm:text-[56px] lg:text-[68px]" data-id="element-595" data-mp="3dud9vycd67li">
              {hero.title}
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pb-2" data-id="element-596" data-mp="1klrk0ymya0mv">
            <p className="max-w-[480px] text-[16px] leading-relaxed text-charcoal/75 sm:text-[17px]" data-id="element-597" data-mp="1yjy7dku9wi8a">{hero.intro}</p>
            <a href={hero.primary.href} className="focus-ring group mt-7 inline-flex min-h-[52px] items-center justify-center gap-3 bg-olive px-7 text-[15px] font-medium text-ivory transition-colors duration-150 hover:bg-charcoal" data-id="element-598" data-mp="1uh1mfnh9ga16">
              {hero.primary.label}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-599" data-mp="2i6dswfl7e3zs" />
            </a>
          </div>
        </div>
      </section>

      {/* Curated gallery */}
      <section aria-label="Selected residential projects" className="bg-ivory pb-14 md:pb-20 lg:pb-24" data-id="element-600" data-mp="2m9aaq42x5w2o">
        <div className="site-container grid gap-10 lg:grid-cols-12 lg:gap-8" data-id="element-601" data-mp="1jw4gfo9v1n8t">
          <div className="lg:col-span-8" data-id="element-602" data-mp="1ju1gabezudqd">
            <ProjectFigure project={featured} frameClass="aspect-[4/3] lg:aspect-auto lg:h-[680px]" large onOpen={setActiveId} data-id="element-603" data-mp="1ou5u1toioucw" />
          </div>
          <div className="grid gap-10 sm:grid-cols-2 sm:gap-6 lg:col-span-4 lg:flex lg:flex-col lg:gap-8" data-id="element-604" data-mp="1xo2hrcxbyeuf">
            {supporting.map(project => <div key={project.id} className="flex flex-1 flex-col" data-id="element-605" data-mp="27lgje6elgz2o">
                <ProjectFigure project={project} frameClass="aspect-[4/3] lg:aspect-auto lg:min-h-[240px] lg:flex-1" onOpen={setActiveId} data-id="element-606" data-mp="27jm1l0ymdn2t" />
              </div>)}
          </div>
        </div>
      </section>

      {/* Recognition link */}
      <section aria-labelledby="gallery-recognition-title" className="border-y border-charcoal/10 bg-stone/70" data-id="element-607" data-mp="i8va9qw636rb">
        <div className="site-container flex flex-col gap-4 py-10 md:py-12 lg:flex-row lg:items-center lg:justify-between lg:gap-12" data-id="element-608" data-mp="eob9lcxr81fk">
          <div className="max-w-[760px]" data-id="element-609" data-mp="9wtjjjx9rl9d">
            <h2 id="gallery-recognition-title" className="font-display text-[26px] font-medium leading-[1.15] text-charcoal sm:text-[30px]" data-id="element-610" data-mp="9vrzwr26tqg1">
              {recognition.title}
            </h2>
            <p className="mt-2 text-[16px] leading-relaxed text-charcoal/75" data-id="element-611" data-mp="v3idmdl57ye">{recognition.text}</p>
          </div>
          <a href={recognition.action.href} className="focus-ring group inline-flex min-h-[44px] shrink-0 items-center gap-2 text-[15px] font-medium text-olive" data-id="element-612" data-mp="xl3129ng9mup">
            <span className="border-b border-olive/35 pb-0.5 transition-colors duration-150 group-hover:border-olive" data-id="element-613" data-mp="oolp5479hkxw">
              {recognition.action.label}
            </span>
            <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-614" data-mp="39eb2tdw8b0p0" />
          </a>
        </div>
      </section>

      <Modal open={!!active} onClose={close} labelledBy="gallery-lightbox-title" describedBy="gallery-lightbox-caption" size="xl" tone="dark" data-id="element-615" data-mp="w344fqqhi08s">
        {active ? <div data-id="element-616" data-mp="n5tr7zu3w118">
            <div className="flex justify-center bg-charcoal" data-id="element-617" data-mp="n6db3m3bgzhw">
              <img src={active.image} alt={active.alt} className="block h-auto max-h-[72vh] w-auto max-w-full" data-id="element-618" data-mp="r5cev3dbaq7r" />
            </div>
            <div className="px-6 py-6 sm:px-9" data-id="element-619" data-mp="1ew7zngjcol5i">
              <h2 id="gallery-lightbox-title" className="font-display text-[30px] font-medium leading-tight" data-id="element-620" data-mp="1juj9hr9navnq">
                {active.location}
              </h2>
              <p id="gallery-lightbox-caption" className="mt-1 text-[15px] text-ivory/75" data-id="element-621" data-mp="15x4gkck6o5r0">
                {active.caption}
              </p>
            </div>
          </div> : null}
      </Modal>
    </>;
}
function ProjectFigure({
  project,
  frameClass,
  large = false,
  onOpen
}: {
  project: GalleryProject;
  frameClass: string;
  large?: boolean;
  onOpen: (id: string) => void;
}) {
  return <figure className="flex h-full flex-1 flex-col" data-id="element-622" data-mp="3n565x1nc17ec">
      <button type="button" onClick={() => onOpen(project.id)} aria-haspopup="dialog" aria-label={`View larger: ${project.location} — ${project.caption}`} className={`focus-ring group relative block w-full overflow-hidden bg-stone ${frameClass}`} data-id="element-623" data-mp="3j0ydatdr77au">
        <img src={project.image} alt={project.alt} loading={large ? 'eager' : 'lazy'} decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]" data-id="element-624" data-mp="34dvduh5veokv" />
        <span className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center bg-ivory/95 text-charcoal opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-625" data-mp="39bn5da2ls8oj">
          <ExpandIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-626" data-mp="2gmq14av88ces" />
        </span>
      </button>
      <figcaption className="mt-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-charcoal/15 pt-4" data-id="element-627" data-mp="kjurhvj0cwzd">
        <span className={`font-display font-medium leading-tight text-charcoal ${large ? 'text-[30px]' : 'text-[24px]'}`} data-id="element-628" data-mp="u9weejzmk9cp">
          {project.location}
        </span>
        <span className="text-[15px] text-charcoal/70" data-id="element-629" data-mp="yaq33fkw5s6n">{project.caption}</span>
      </figcaption>
    </figure>;
}